import React, { useState } from 'react';
import type { UiMessage, FeedbackType } from '../types';
import type { ContextItem } from '../../../services/chatApi';
import * as Constants from '../../../constants';
import './MessageList.css';

interface MessageListProps {
  messages: UiMessage[];
  waiting: boolean;
  feedbackGiven: Record<string, FeedbackType | undefined>;
  onAskFeedback: (messageId: string, type: FeedbackType) => void;
  onShowSources: (sources: ContextItem[]) => void;
  onReplyWith: (text: string) => void;
  renderMessage?: (message: string, role: string) => React.ReactNode;
}

const defaultRenderMessage = (text: string) => <div>{text}</div>;

function MessageListComponent({ 
  messages, 
  waiting, 
  feedbackGiven, 
  onAskFeedback, 
  onShowSources, 
  onReplyWith,
  renderMessage = defaultRenderMessage
}: MessageListProps) {
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());

  const toggleExpanded = (messageId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const extractMailContent = (text: string) => {
    const startMarker = '[[EMAIL_CONTEXT_START]]';
    const endMarker = '[[EMAIL_CONTEXT_END]]';
    
    const startIndex = text.indexOf(startMarker);
    const endIndex = text.indexOf(endMarker);
    
    if (startIndex !== -1 && endIndex !== -1) {
      const beforeMail = text.substring(0, startIndex);
      const mailContent = text.substring(startIndex, endIndex + endMarker.length);
      const afterMail = text.substring(endIndex + endMarker.length);
      
      return {
        hasMailContent: true,
        beforeMail: beforeMail.trim(),
        mailContent,
        afterMail: afterMail.trim(),
        userMessage: beforeMail.trim() + (afterMail.trim() ? '\n' + afterMail.trim() : '')
      };
    }
    
    return { hasMailContent: false, userMessage: text };
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 96 }}>
      {messages.map((m) => {
        const isExpanded = expandedMessages.has(m.id);
        const mailData = m.role === 'user' ? extractMailContent(m.text) : { hasMailContent: false, userMessage: m.text };

        return (
          <div key={m.id} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', margin: '6px 0' }}>
            <div style={{ maxWidth: '80%', padding: '10px 12px', borderRadius: 12, background: m.role === 'user' ? Constants.COLOR_BOMA_BLUE : '#f2f4f8', color: m.role === 'user' ? '#fff' : '#111' }}>
              {mailData.hasMailContent && !isExpanded ? (
                <div>
                  <div className="message-content" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 8 }}>
                    {renderMessage(mailData.userMessage, m.role)}
                  </div>
                  <div style={{ fontStyle: 'italic', opacity: 0.7, fontSize: '13px' }}>
                    [Mailcontent verborgen]
                    <button 
                      onClick={() => toggleExpanded(m.id)}
                      style={{ 
                        marginLeft: 8, 
                        border: '1px solid currentColor', 
                        background: 'transparent', 
                        color: 'inherit', 
                        cursor: 'pointer', 
                        padding: '2px 8px', 
                        borderRadius: 4,
                        fontSize: '12px'
                      }}
                    >
                      Toon
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {mailData.hasMailContent && (
                    <button 
                      onClick={() => toggleExpanded(m.id)}
                      style={{ 
                        marginBottom: 8,
                        border: '1px solid currentColor', 
                        background: 'transparent', 
                        color: 'inherit', 
                        cursor: 'pointer', 
                        padding: '2px 8px', 
                        borderRadius: 4,
                        fontSize: '12px'
                      }}
                    >
                      Verberg mailcontent
                    </button>
                  )}
                  <div className="message-content" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {renderMessage(m.text, m.role)}
                  </div>
                </>
              )}
              {m.role === 'assistant' && (
                <div className="msg-actions" style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
                  <button onClick={() => onAskFeedback(m.id, 'up')} title="Goed antwoord" style={{ border: 0, background: 'transparent', cursor: 'pointer', color: feedbackGiven[m.id] === 'up' ? '#1f74ff' : '#99a1ad' }}>👍</button>
                  <button onClick={() => onAskFeedback(m.id, 'down')} title="Kan beter" style={{ border: 0, background: 'transparent', cursor: 'pointer', color: feedbackGiven[m.id] === 'down' ? '#d32f2f' : '#99a1ad' }}>👎</button>
                  {m.sources && m.sources.length > 0 && (
                    <button onClick={() => onShowSources(m.sources!)} title="Toon bronnen" style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#1f74ff' }}>
                      📄 Bronnen ({m.sources.length})
                    </button>
                  )}
                  <button
                    className="icon-btn"
                    title="Zet in reply"
                    aria-label="Zet in reply"
                    onClick={() => onReplyWith?.(m.text)}
                    style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#1f74ff' }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M10 8V5l-7 7 7 7v-3c6 0 9-2.5 11-7-2.5 2-5 3-11 3Z"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {waiting && (
        <div className="typing-indicator" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, color: '#666', fontStyle: 'italic' }}>
          <img
            className="typing-indicator__mascot"
            src={`${process.env.PUBLIC_URL}/assets/boma_mascot_Thinking.png`}
            alt="Bo is aan het nadenken"
            style={{ width: 32, height: 32 }}
          />
          <span>Bo denkt na…</span>
        </div>
      )}
    </div>
  );
}

export const MessageList = React.memo(MessageListComponent);